/**
 * Museum-flavored homepage sections.
 *
 * Six section types seeded by db-setup.ts when siteType === 'museum':
 *   - current_exhibit_hero — hero variant featuring an on-view exhibit
 *   - visit_card           — hours, address, admission with CTA to /page/visit
 *   - events_teaser        — next N upcoming public events (placeholder until
 *                            Phase 2 ships the events feature)
 *   - mission_brief        — short mission statement card with CTA
 *   - donate_cta           — donation appeal with suggested amounts
 *   - membership_tiers     — membership tier comparison (placeholder until
 *                            Phase 3 ships the membership feature)
 *
 * Each component receives the homepage_sections row as `section`. The
 * `section.content` JSON shape is described inline. All admin editing of these
 * sections (Phase 1) is via raw JSON in the homepage builder's edit modal —
 * dedicated form-style editors are a follow-up.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';

interface SectionProps {
  section: {
    id?: string;
    title?: string | null;
    subtitle?: string | null;
    content?: Record<string, any> | null;
  };
}

// ---------------------------------------------------------------------------
// current_exhibit_hero
// content: { exhibit_title, exhibit_subtitle, hero_image, cta_label, cta_href }
// ---------------------------------------------------------------------------

export function CurrentExhibitHero({ section }: SectionProps) {
  const { t } = useTranslation('museum');
  const c = section.content || {};
  const heroImage = c.hero_image || null;
  return (
    <section className="relative overflow-hidden">
      <div
        className="relative bg-stone-900 text-white" /* dark-exempt: intentional fixed-dark hero band */
        style={
          heroImage
            ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.45),rgba(0,0,0,0.55)),url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : undefined
        }
      >
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <p className="uppercase tracking-widest text-xs text-white/70 mb-2">{section.subtitle || t('exhibitHero.onViewNow')}</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 max-w-3xl">{c.exhibit_title || section.title}</h1>
          {c.exhibit_subtitle && <p className="text-lg md:text-xl text-white/85 max-w-2xl mb-6">{c.exhibit_subtitle}</p>}
          {c.cta_label && c.cta_href && (
            <Link
              to={c.cta_href}
              className="inline-block px-6 py-3 bg-surface text-ink font-medium rounded-md hover:bg-surface-muted transition-colors"
            >
              {c.cta_label}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// visit_card
// content: { address, hours, admission, cta_label, cta_href }
// hours/admission can be an array of { day, open, from, to } / { label, price }
// or a free-form string. Both shapes are rendered.
// ---------------------------------------------------------------------------

interface HoursRow { day: string; open?: boolean; from?: string | null; to?: string | null }
interface AdmissionRow { label: string; price: string }

function isHoursArray(h: any): h is HoursRow[] { return Array.isArray(h) && h.length > 0 && typeof h[0]?.day === 'string'; }
function isAdmissionArray(a: any): a is AdmissionRow[] { return Array.isArray(a) && a.length > 0 && typeof a[0]?.label === 'string'; }

export function VisitCard({ section }: SectionProps) {
  const { t } = useTranslation('museum');
  const c = section.content || {};
  return (
    <section className="bg-surface-soft py-12">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-ink mb-2">{section.title || t('visit.heading')}</h2>
          {section.subtitle && <p className="text-ink-soft">{section.subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-surface rounded-lg p-8 shadow-sm border border-line">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">{t('visit.hours')}</h3>
            {isHoursArray(c.hours) ? (
              <ul className="space-y-1.5 text-sm">
                {c.hours.map((h: HoursRow) => (
                  <li key={h.day} className="flex justify-between">
                    <span className="text-ink-soft">{h.day}</span>
                    <span className={h.open ? 'text-ink' : 'text-ink-muted'}>
                      {h.open && h.from && h.to ? `${h.from}–${h.to}` : t('visit.closed')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-soft whitespace-pre-line">{typeof c.hours === 'string' ? c.hours : t('visit.hoursTBD')}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">{t('visit.admission')}</h3>
            {isAdmissionArray(c.admission) ? (
              <ul className="space-y-1.5 text-sm">
                {c.admission.map((a: AdmissionRow) => (
                  <li key={a.label} className="flex justify-between">
                    <span className="text-ink-soft">{a.label}</span>
                    <span className="text-ink font-medium">{a.price}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-soft whitespace-pre-line">{typeof c.admission === 'string' ? c.admission : t('visit.admissionTBD')}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">{t('visit.address')}</h3>
            <p className="text-sm text-ink-soft whitespace-pre-line">{c.address || t('visit.addressFallback')}</p>
            {c.cta_href && (
              <Link
                to={c.cta_href}
                className="inline-block mt-4 text-sm font-medium text-amber-700 hover:text-amber-800 underline"
              >
                {c.cta_label || t('visit.planVisitCta')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// events_teaser
// content: { count?: number, show_past?: boolean }
// Fetches the next N upcoming public events from /api/events. Falls back to
// an empty state when the museum hasn't published any events yet.
// ---------------------------------------------------------------------------

interface PublicEventTeaser {
  id: string;
  slug: string;
  title: string;
  starts_at: string;
  location_text?: string;
}

export function EventsTeaser({ section }: SectionProps) {
  const { t, i18n } = useTranslation('museum');
  const c = section.content || {};
  const count = typeof c.count === 'number' ? c.count : 3;

  const [events, setEvents] = useState<PublicEventTeaser[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ events: PublicEventTeaser[] }>(`/events?limit=${count}`)
      .then((res) => {
        if (!cancelled) setEvents(res.events || []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => { cancelled = true; };
  }, [count]);

  return (
    <section className="py-12">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-ink">{section.title || t('events.heading')}</h2>
            {section.subtitle && <p className="text-ink-soft mt-1">{section.subtitle}</p>}
          </div>
          <Link to="/events" className="text-sm font-medium text-amber-700 hover:text-amber-800 underline">
            {t('events.allEvents')}
          </Link>
        </div>
        {events === null ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className="rounded-lg border border-line bg-surface-soft p-6 animate-pulse h-32" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="text-ink-muted text-center py-8">{t('events.empty')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {events.map((e) => {
              const d = new Date(e.starts_at);
              return (
                <Link
                  key={e.id}
                  to={`/events/${e.slug}`}
                  className="rounded-lg border border-line bg-surface p-5 hover:shadow-md transition-shadow group"
                >
                  <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">
                    {d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                    {' · '}
                    {d.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' })}
                  </p>
                  <h3 className="font-semibold text-ink mb-1 group-hover:text-amber-700 transition-colors">{e.title}</h3>
                  {e.location_text && <p className="text-sm text-ink-soft">{e.location_text}</p>}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// mission_brief
// content: { body, image, cta_label, cta_href }
// ---------------------------------------------------------------------------

export function MissionBrief({ section }: SectionProps) {
  const { t } = useTranslation('museum');
  const c = section.content || {};
  return (
    <section className="bg-surface-soft py-14">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-ink mb-4">{section.title || t('mission.heading')}</h2>
        <p className="text-lg text-ink-soft leading-relaxed mb-6 max-w-2xl mx-auto">{c.body || section.subtitle}</p>
        {c.cta_label && c.cta_href && (
          <Link
            to={c.cta_href}
            className="inline-block px-6 py-2.5 border border-ink text-ink font-medium rounded-md hover:bg-ink hover:text-surface transition-colors"
          >
            {c.cta_label}
          </Link>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// donate_cta
// content: { body, suggested_amounts: number[], cta_label }
// ---------------------------------------------------------------------------

export function DonateCta({ section }: SectionProps) {
  const { t } = useTranslation('museum');
  const c = section.content || {};
  const amounts: number[] = Array.isArray(c.suggested_amounts) ? c.suggested_amounts : [25, 50, 100, 250];
  return (
    <section className="py-14 bg-amber-50">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-ink mb-3">{section.title || t('donate.heading')}</h2>
        {section.subtitle && <p className="text-ink-soft mb-2">{section.subtitle}</p>}
        {c.body && <p className="text-ink-soft mb-6">{c.body}</p>}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {amounts.map((amt) => (
            <Link
              key={amt}
              to={`/donate?amount=${amt}`}
              className="px-5 py-2 bg-surface border border-amber-700 text-amber-800 font-semibold rounded-md hover:bg-amber-700 hover:text-white transition-colors"
            >
              ${amt}
            </Link>
          ))}
          <Link
            to="/donate"
            className="px-5 py-2 bg-surface border border-amber-700 text-amber-800 font-semibold rounded-md hover:bg-amber-700 hover:text-white transition-colors"
          >
            {t('donate.other')}
          </Link>
        </div>
        <Link
          to="/donate"
          className="inline-block px-8 py-3 bg-amber-700 text-white font-semibold rounded-md hover:bg-amber-800 transition-colors"
        >
          {c.cta_label || t('donate.donateNow')}
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// membership_tiers
// content: { show_n_tiers?: number }
// Fetches the active tiers from /api/membership/tiers. The first N tiers
// (display_order asc) are spotlighted on the homepage; full comparison
// lives at /membership.
// ---------------------------------------------------------------------------

interface PublicTier {
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  price_amount: number | string;
  price_interval: 'one_time' | 'month' | 'year' | 'quarter';
  benefits?: { label: string; description?: string }[];
}

export function MembershipTiersSection({ section }: SectionProps) {
  const { t } = useTranslation('museum');
  const c = section.content || {};
  const n = typeof c.show_n_tiers === 'number' ? c.show_n_tiers : 3;

  const [tiers, setTiers] = useState<PublicTier[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ tiers: PublicTier[] }>('/membership/tiers')
      .then((res) => {
        if (!cancelled) setTiers((res.tiers || []).slice(0, n));
      })
      .catch(() => { if (!cancelled) setTiers([]); });
    return () => { cancelled = true; };
  }, [n]);

  if (tiers !== null && tiers.length === 0) {
    // Don't render the section at all if there are no tiers configured yet.
    return null;
  }

  function tierIntervalLabel(i: string): string {
    if (i === 'month') return t('membership.intervals.month');
    if (i === 'year') return t('membership.intervals.year');
    if (i === 'quarter') return t('membership.intervals.quarter');
    return t('membership.intervals.oneTime');
  }

  return (
    <section className="py-14">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-ink">{section.title || t('membership.heading')}</h2>
          {section.subtitle && <p className="text-ink-soft mt-2">{section.subtitle}</p>}
        </div>
        {tiers === null ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {Array.from({ length: n }).map((_, i) => (
              <div key={i} className="rounded-lg border border-line bg-surface-soft p-6 animate-pulse h-48" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {tiers.map((tier) => (
              <Link
                key={tier.id}
                to={`/membership`}
                className="rounded-lg border border-line bg-surface p-6 text-center hover:border-amber-500 hover:shadow-md transition-all"
              >
                {tier.tagline && <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">{tier.tagline}</p>}
                <h3 className="text-xl font-bold text-ink mb-2">{tier.name}</h3>
                <p className="text-2xl font-bold text-ink mb-2">
                  ${Number(tier.price_amount).toFixed(0)}
                  <span className="text-sm text-ink-muted font-normal">{tierIntervalLabel(tier.price_interval)}</span>
                </p>
                {tier.benefits && tier.benefits.length > 0 && (
                  <p className="text-xs text-ink-soft line-clamp-3">
                    {tier.benefits.slice(0, 3).map((b) => b.label).join(' · ')}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
        <div className="text-center mt-6">
          <Link to="/membership" className="text-sm font-medium text-amber-700 hover:text-amber-800 underline">
            {t('membership.compareAll')}
          </Link>
        </div>
      </div>
    </section>
  );
}
